import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { connect } from 'react-redux';
import { getAnalytics, logEvent } from 'firebase/analytics';
import { ArrowBackIosNew, History, TuneOutlined } from '@mui/icons-material';
import { useLocation, useNavigate } from 'react-router-dom';

import { AskSessionBoundary, useAskSession } from '../components/AskSession';
import HomeSuggestions from '../components/HomeSuggestions';
import BottomMenuLegalNote from '../components/BottomMenuLegalNote';
import TopBar from '../components/TopBar';
import BottomMenu from '../components/BottomMenu';
import BlurPillButton from '../components/BlurPillButton';
import SectionActionButton from '../components/SectionActionButton';
import ExploreLoggedIn from '../components/ExploreLoggedIn';
import PreviewInfosLoading from '../components/PreviewInfosLoading';
import GetStartedPickSpeakers from '../components/GetStartedPickSpeakers';
import { navigateToSigninWithRedirect } from '../components/signinUtils';
import { CHAT_V2_MAX_INPUT_CHARACTERS } from '../services/chatV2';
import { useAskNavigation } from '../utils/askNavigation';
import { useDesktopAsk } from '../utils/desktopAsk';
import { useHomeAskClearance } from '../utils/useHomeAskClearance';
import { ONBOARDING_HOME_PICKS_UPDATED, clearOnboardingHomePicks, clearOnboardingSession, readOnboardingHomePicks, readOnboardingSession } from '../utils/onboardingSession';
import { trackOnboardingEvent } from '../utils/onboardingFunnel';
import { retryAuthSession } from '../utils/restoreAuthSession';
import { getAppScrollTarget, getAppViewport } from '../utils/appViewport';

export { resetHomeSuggestionVisitOffsets } from '../components/AskSession';

const PLACEHOLDER_TEXT = 'Whats on your mind?';
const MAX_HEIGHT = 240;

// visualViewport describes the area actually above the mobile browser keyboard.
// Desktop and browsers without it use the normal viewport, with no keyboard gap.
const useAskViewport = (focused) => {
    const [viewport, setViewport] = useState(null);
    useLayoutEffect(() => {
        if (!focused) return undefined;
        const visible = window.visualViewport;
        const update = () => {
            const framed = getAppScrollTarget() !== window;
            setViewport({
                height: framed ? getAppViewport().height : visible?.height || window.innerHeight,
                top: framed ? 0 : visible?.offsetTop || 0,
                width: window.innerWidth,
            });
        };
        update();
        visible?.addEventListener('resize', update);
        visible?.addEventListener('scroll', update);
        window.addEventListener('resize', update);
        return () => {
            visible?.removeEventListener('resize', update);
            visible?.removeEventListener('scroll', update);
            window.removeEventListener('resize', update);
        };
    }, [focused]);
    return viewport;
};

const useHomeBottomClearance = (pageRef, menuRef, selectedCount, focused, signedOut) => {
    const [clearance, setClearance] = useState({ nav: 0, content: 0 });
    const menuHidden = signedOut && selectedCount > 0;
    useLayoutEffect(() => {
        if (focused) return undefined;
        const menu = menuRef.current;
        const dock = pageRef.current?.querySelector('.getStartedContinueDock');
        const measure = () => {
            const nav = menuHidden ? 0 : menu?.getBoundingClientRect().height || 0;
            const dockRect = dock?.getBoundingClientRect();
            // Retained Ask backgrounds have no geometry; keep their last clearance.
            if ((!menuHidden && !nav) || (dock && !dockRect?.height)) return;
            const content = dockRect?.height > 0
                ? Math.max(nav, getAppViewport().bottom - dockRect.top)
                : nav;
            setClearance((old) => old.nav === nav && old.content === content ? old : { nav, content });
        };
        measure();
        const observer = typeof ResizeObserver === 'function' ? new ResizeObserver(measure) : null;
        if (menu) observer?.observe(menu);
        if (dock) observer?.observe(dock);
        window.addEventListener('resize', measure);
        return () => {
            observer?.disconnect();
            window.removeEventListener('resize', measure);
        };
    }, [pageRef, menuRef, selectedCount, focused, signedOut, menuHidden]);
    const fallbackClearance = `calc(${signedOut ? 110 : 70}px + env(safe-area-inset-bottom))`;
    return {
        '--home-nav-height': menuHidden ? '0px' : clearance.nav ? `${clearance.nav}px` : fallbackClearance,
        '--home-bottom-clearance': menuHidden || clearance.content ? `${clearance.content}px` : fallbackClearance,
    };
};

const HomeScreen = ({ authenticated, uid, authRestoreError, retryAuthSession, focused = false }) => {
    const session = useAskSession();
    const location = useLocation();
    const navigate = useNavigate();
    const editing = focused || location.pathname === '/ask';
    const desktop = useDesktopAsk();
    const desktopHome = desktop && !editing;
    const { openAsk, closeAsk, originLabel } = useAskNavigation();
    const viewport = useAskViewport(editing);
    const textareaRef = useRef(null);
    const focusedBodyRef = useRef(null);
    const focusedSuggestionsRef = useRef(null);
    const pageRef = useRef(null);
    const menuRef = useRef(null);
    const askDockRef = useRef(null);
    const [mobileSuggestionsVisible, setMobileSuggestionsVisible] = useState(true);
    const [selectedCount, setSelectedCount] = useState(0);
    const [homePicks, setHomePicks] = useState(() => ({
        uid,
        previews: authenticated === true ? readOnboardingHomePicks(uid) : [],
    }));
    const signedOut = authenticated === false;
    const selectingSpeakers = signedOut && selectedCount > 0;
    useEffect(() => {
        if (signedOut && !editing) trackOnboardingEvent('onboarding_home_view', {}, { start: true });
    }, [editing, signedOut]);
    useEffect(() => {
        const onboarding = readOnboardingSession();
        if ((location.pathname === '/' || location.pathname === '/home')
            && !(onboarding?.savePending && !onboarding.guestMigrated)) clearOnboardingSession();
    }, [location.pathname]);
    useEffect(() => {
        if (authenticated === false) clearOnboardingHomePicks();
        setHomePicks({ uid, previews: authenticated === true ? readOnboardingHomePicks(uid) : [] });
        const updatePicks = event => {
            if (authenticated === true && event.detail?.uid === uid) {
                setHomePicks({ uid, previews: readOnboardingHomePicks(uid) });
            }
        };
        window.addEventListener(ONBOARDING_HOME_PICKS_UPDATED, updatePicks);
        return () => window.removeEventListener(ONBOARDING_HOME_PICKS_UPDATED, updatePicks);
    }, [authenticated, uid]);
    const authPending = authenticated !== true && !signedOut;
    useHomeAskClearance({ pageRef, dockRef: askDockRef, editing });
    const clearance = useHomeBottomClearance(pageRef, menuRef, selectedCount, editing, signedOut);
    const { text, hasText, submitting, errorMessage, suggestionActionIsPending } = session;
    // Keep the member layout in place while the saved sign-in is restored.
    // Authentication itself stays unresolved until the account request completes.
    const memberHome = !signedOut && !editing;
    const showSuggestions = (desktopHome || editing || !hasText) && (session.loading || session.focusedSuggestions.length > 0);

    useEffect(() => {
        logEvent(getAnalytics(), 'screen_view', { firebase_screen: editing ? 'ask' : 'home' });
    }, [editing]);

    useLayoutEffect(() => {
        if (!editing || !textareaRef.current) return;
        textareaRef.current.focus({ preventScroll: true });
        const end = textareaRef.current.value.length;
        textareaRef.current.setSelectionRange(end, end);
    }, [editing, desktop]);

    useLayoutEffect(() => {
        if (!editing && !desktopHome) return undefined;
        const resize = () => {
            const input = textareaRef.current;
            if (!input) return;
            input.style.height = 'auto';
            const naturalInputHeight = input.scrollHeight;
            let maxHeight = MAX_HEIGHT;
            const body = focusedBodyRef.current;
            if (desktopHome && askDockRef.current) {
                // A short window must still fit the guest header, Send and the
                // bottom navigation when the fixed input contains a long draft.
                const chromeHeight = askDockRef.current.offsetHeight - input.offsetHeight;
                const navigationHeight = menuRef.current?.offsetHeight || 80;
                maxHeight = Math.max(44, Math.min(MAX_HEIGHT, getAppViewport().height - chromeHeight - navigationHeight - 12));
            } else if (editing && !desktop && body?.clientHeight) {
                // The keyboard can leave less room than the usual textarea cap.
                // Reserve the composer border, padding, margins and any status.
                const footer = input.closest('.homeChatBoxFooter');
                const bodyStyle = window.getComputedStyle(body);
                const bodyPadding = (parseFloat(bodyStyle.paddingTop) || 0) + (parseFloat(bodyStyle.paddingBottom) || 0);
                const chromeHeight = footer.offsetHeight - input.offsetHeight;
                maxHeight = Math.max(0, Math.min(MAX_HEIGHT, body.clientHeight - bodyPadding - chromeHeight));
            }
            input.style.height = `${Math.min(naturalInputHeight, maxHeight)}px`;

            const suggestionsPane = focusedSuggestionsRef.current;
            if (suggestionsPane) {
                const inputStyle = window.getComputedStyle(input);
                const lineHeight = parseFloat(inputStyle.lineHeight);
                const inputPadding = (parseFloat(inputStyle.paddingTop) || 0) + (parseFloat(inputStyle.paddingBottom) || 0);
                const explicitLines = input.value.split('\n').length;
                // Measure before the height cap so wrapping and long pasted
                // drafts behave the same, even in a small keyboard viewport.
                const lines = Math.max(explicitLines, lineHeight > 0
                    ? Math.round((naturalInputHeight - inputPadding) / lineHeight) : 1);
                const list = suggestionsPane.firstElementChild;
                const listHeight = list ? list.offsetHeight + (parseFloat(window.getComputedStyle(list).marginBottom) || 0) : 0;
                const fits = !body?.clientHeight || listHeight <= suggestionsPane.clientHeight + 1;
                // Hide the whole pair at three lines; wait until one line to
                // restore it so edits around a wrap boundary do not flicker.
                setMobileSuggestionsVisible((visible) => fits && lines < (visible ? 3 : 2));
            }
        };
        resize();
        const observer = editing && !desktop && typeof ResizeObserver === 'function'
            ? new ResizeObserver(resize) : null;
        if (focusedBodyRef.current) observer?.observe(focusedBodyRef.current);
        if (focusedSuggestionsRef.current?.firstElementChild) observer?.observe(focusedSuggestionsRef.current.firstElementChild);
        window.addEventListener('resize', resize);
        return () => {
            observer?.disconnect();
            window.removeEventListener('resize', resize);
        };
    }, [editing, desktop, desktopHome, signedOut, text, submitting, errorMessage, session.loading, session.focusedSuggestions, viewport?.height, viewport?.width]);

    const signinClick = () => {
        session.prepareSignin();
        logEvent(getAnalytics(), 'signin_click');
        navigateToSigninWithRedirect(navigate, `${location.pathname}${location.search}${location.hash}`, editing, location.state);
    };
    const beginAsking = (event) => {
        // Commit the input in the click's user-activation window (mobile Safari).
        const triggerElement = event.currentTarget;
        flushSync(() => openAsk({ triggerElement }));
    };
    const onKeyDown = (event) => {
        if (event.key === 'Enter' && !event.shiftKey && !event.nativeEvent.isComposing && event.keyCode !== 229) {
            session.onSubmit(event);
        }
        if (event.key === 'Escape') {
            event.preventDefault();
            if (desktopHome) event.currentTarget.blur();
            else closeAsk();
        }
    };
    const chatBox = (
        <div className='chatBoxFooter homeChatBoxFooter'>
            <div className='chatBox homeChatBox'>
                <form onSubmit={session.onSubmit}>
                    <div className={`chatInputWrap homeChatInputWrap${!editing ? ' homeChatInputWrapCompact' : ''}`}>
                        {editing || desktopHome ? <textarea
                            ref={textareaRef}
                            placeholder={PLACEHOLDER_TEXT}
                            className='chatInput hideScrollbar homeChatInput'
                            aria-label={PLACEHOLDER_TEXT}
                            rows={1}
                            maxLength={CHAT_V2_MAX_INPUT_CHARACTERS}
                            value={text}
                            onChange={session.onChange}
                            onKeyDown={onKeyDown}
                            disabled={submitting}
                        /> : <button
                            type='button'
                            className={`chatInput homeChatInput homeAskEntry${hasText ? ' homeAskEntryHasText' : ''}`}
                            aria-label={hasText ? `${PLACEHOLDER_TEXT} ${text}` : PLACEHOLDER_TEXT}
                            onClick={beginAsking}
                            disabled={suggestionActionIsPending}
                        >{hasText ? text : PLACEHOLDER_TEXT}</button>}
                        {(authenticated === true || memberHome) && !hasText && (editing || !submitting) ? <button
                            type='button'
                            className='homeUtilityButton homeUtilityIconButton homeChatHistoryButton'
                            onClick={session.pastChatsClick}
                            aria-label='History'
                            disabled={authPending || suggestionActionIsPending}
                        ><History aria-hidden='true' style={{ fontSize: '22px' }} /></button> : <button
                            type='submit'
                            className={`chatSubmitBtn chatSubmitBtnVisible homeChatSubmitBtn${hasText ? '' : ' homeChatSubmitBtnIdle'}`}
                            aria-label='Send'
                            disabled={!hasText || suggestionActionIsPending}
                            style={{ opacity: submitting ? 0.6 : 1 }}
                        >
                            <svg aria-hidden='true' focusable='false' width='34' height='34' viewBox='0 0 24 24' fill='none'>
                                <path d='M5 12h12m0 0-5-5m5 5-5 5' stroke='currentColor' strokeWidth='2.4' strokeLinecap='round' strokeLinejoin='round' />
                            </svg>
                        </button>}
                    </div>
                </form>
            </div>
            {(submitting || errorMessage) && <div className='homeAskStatus' role={errorMessage ? 'alert' : 'status'}>
                {errorMessage || 'Starting chat...'}
            </div>}
        </div>
    );
    const suggestions = showSuggestions && <HomeSuggestions
        suggestions={session.focusedSuggestions}
        loading={session.loading}
        disabled={suggestionActionIsPending}
        dismissingSuggestionIdentity={session.dismissingSuggestionIdentity}
        onSelectSuggestion={session.selectFocusedSuggestion}
        onDismissSuggestion={session.dismissFocusedSuggestion}
    />;
    const focusedComposer = (
        <div className='homeChatFooterRegion'>
            {desktop && chatBox}
            {!desktop ? <div
                className='focusedAskSuggestions'
                ref={focusedSuggestionsRef}
                aria-hidden={mobileSuggestionsVisible ? undefined : 'true'}
                inert={mobileSuggestionsVisible ? undefined : ''}
            >{suggestions}</div> : suggestions}
            {!desktop && chatBox}
        </div>
    );

    if (editing) return (
        <main className={`container appScreen focusedAskPage${desktop ? ' focusedAskPageDesktop' : ' focusedAskPageMobile'}`} style={viewport ? { height: `${viewport.height}px`, top: `${viewport.top}px` } : undefined}>
            <header className='focusedAskHeader'>
                <button type='button' className='focusedAskBack' onClick={closeAsk} aria-label={`Back to ${originLabel}`}>
                    <ArrowBackIosNew aria-hidden='true' style={{ fontSize: '20px' }} />{originLabel}
                </button>
                {signedOut && <BlurPillButton label='Sign in' onClick={signinClick} />}
            </header>
            <div className='focusedAskBody' ref={focusedBodyRef}>{focusedComposer}</div>
        </main>
    );

    return (
        <div ref={pageRef} className={`container appScreen homeMergedPage${desktopHome ? ' homeDesktopPage' : ''}${memberHome ? ' homeMemberPage' : ''}${signedOut ? ' signedOutExplorePage' : ''}${selectingSpeakers ? ' signedOutExplorePageHasSelection' : ''}`} style={clearance}>
            <main className='homePage' aria-busy={authPending && !authRestoreError}>
                {memberHome && <h1 className='visuallyHidden'>Home</h1>}
                <div ref={askDockRef} className='homePinnedAsk' data-home-ask-dock data-home-pinned-ask>
                    {signedOut && <header className='homeGuestHeader' data-home-header>
                        <TopBar signinClick={signinClick} horizontalInset={24} />
                    </header>}
                    {chatBox}
                </div>
                {signedOut && <div className='homeMergedIntroduction'>
                    <h1 className='chatTitle homeHeroTitle getStartedPickerTitle'>Hear from humans</h1>
                    <p className='homeHeroSubtitle getStartedPickerSubtitle'>Podcast highlights on any topic</p>
                </div>}
                <div className='homeChatFooterRegion'>{suggestions}</div>
                <section id='explore' aria-label={signedOut ? 'Pick your people' : 'For you'}>
                    {signedOut && <GetStartedPickSpeakers embeddedInHome onSelectionChange={setSelectedCount} />}
                    {memberHome && <>
                        <div className='explorePageHeader homeExploreHeader'>
                            <h2 className='homeDiscoveryHeading'>For you</h2>
                            <SectionActionButton
                                iconOnly
                                aria-label='Customize your feed'
                                title='Customize your feed'
                                disabled={authPending}
                                onClick={() => navigate('/settings')}
                            >
                                <TuneOutlined aria-hidden='true' />
                            </SectionActionButton>
                        </div>
                        {authPending && authRestoreError ? <div role='alert' className='homeAskStatus'>
                            <span>{authRestoreError}</span>
                            <BlurPillButton onClick={retryAuthSession}>Retry</BlurPillButton>
                        </div> : authPending ? <div role='status' aria-label='Loading your feed'>
                            <PreviewInfosLoading />
                        </div> : <ExploreLoggedIn initialPreviews={homePicks.uid === uid ? homePicks.previews : []} />}
                    </>}
                </section>
            </main>
            {!selectingSpeakers && <BottomMenu reserveMoreTab={authPending} containerRef={menuRef} bottomNote={signedOut ? <span className='homeBottomNote'><BottomMenuLegalNote /></span> : null} />}
        </div>
    );
};

export const Home = ({ authenticated, uid, authRestoreError, retryAuthSession, focused }) => (
    <AskSessionBoundary authenticated={authenticated} uid={uid}>
        <HomeScreen authenticated={authenticated} uid={uid} authRestoreError={authRestoreError} retryAuthSession={retryAuthSession} focused={focused} />
    </AskSessionBoundary>
);

export default connect((state) => ({
    authenticated: state.user.authenticated, uid: state.user.uid, authRestoreError: state.user.authRestoreError,
}), { retryAuthSession })(Home);
